import React, { useState, useEffect } from 'react';
import { X, Copy, Check, AlertTriangle, BarChart3, Code, Target, PlayCircle } from 'lucide-react';
import pako from 'pako';
import { ReportCharts } from './ReportCharts';
import { ReportPracticeGuide } from './ReportPracticeGuide';
import { VisualGuide } from './VisualGuide';

interface ReportViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  compressedData: string | null;
  title?: string;
  lyricsUrl?: string;
  vocalRecordingUrl?: string;
  leadVocalUrl?: string;
  finalRecordingUrl?: string;
}

type TabType = 'charts' | 'practiceGuide' | 'visualGuide' | 'json';

export const ReportViewerModal: React.FC<ReportViewerModalProps> = ({
  isOpen,
  onClose,
  compressedData,
  title = 'Report Data',
  lyricsUrl,
  vocalRecordingUrl,
  leadVocalUrl,
  finalRecordingUrl
}) => {
  const [jsonData, setJsonData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('practiceGuide');

  useEffect(() => {
    if (!isOpen || !compressedData) {
      setJsonData(null);
      setError('');
      setActiveTab('practiceGuide');
      return;
    }

    const decompress = async () => {
      setLoading(true);
      setError('');
      setJsonData(null);

      try {
        const binaryString = atob(compressedData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const decompressed = pako.inflate(bytes);
        const text = new TextDecoder().decode(decompressed);
        const json = JSON.parse(text);

        setJsonData(json);
      } catch (err: any) {
        console.error('Decompression error:', err);
        setError(`Failed to decompress/parse data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    decompress();
  }, [isOpen, compressedData]);

  const handleCopy = async () => {
    if (!jsonData) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'practiceGuide', label: 'Practice Guide', icon: <Target size={16} /> },
    { id: 'visualGuide', label: 'Visual Guide', icon: <PlayCircle size={16} /> },
    { id: 'charts', label: 'Charts', icon: <BarChart3 size={16} /> },
    { id: 'json', label: 'Raw JSON', icon: <Code size={16} /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-kod-purple-800 shadow-2xl w-full h-full flex flex-col border-x border-kod-purple-600">
        <div className="flex items-center justify-between px-6 py-4 border-b border-kod-purple-600 bg-kod-purple-900/50">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <div className="flex items-center gap-2">
            {jsonData && activeTab === 'json' && (
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg hover:bg-kod-purple-700 transition-colors text-white/70 hover:text-white"
                title="Copy JSON"
              >
                {copied ? <Check size={20} className="text-green-400" /> : <Copy size={20} />}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-kod-purple-700 transition-colors text-white/70 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {jsonData && (
          <div className="flex border-b border-kod-purple-600 bg-kod-purple-900/30">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-kod-accent border-b-2 border-kod-accent bg-kod-purple-700/30'
                    : 'text-white/60 hover:text-white hover:bg-kod-purple-700/20'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
              <span className="ml-3 text-white/70">Decompressing data...</span>
            </div>
          )}

          {error && (
            <div className="m-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-400 font-medium">Error</p>
                <p className="text-red-300/80 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {!compressedData && !loading && (
            <div className="text-center py-12 text-white/50">
              No report data available
            </div>
          )}

          {jsonData && activeTab === 'practiceGuide' && (
            <ReportPracticeGuide
              report={jsonData}
              lyricsUrl={lyricsUrl}
              vocalRecordingUrl={vocalRecordingUrl}
              leadVocalUrl={leadVocalUrl}
            />
          )}

          {jsonData && activeTab === 'visualGuide' && (
            <div className="h-[calc(100vh-140px)]">
              <VisualGuide
                report={jsonData}
                lyricsUrl={lyricsUrl}
                vocalRecordingUrl={vocalRecordingUrl}
                leadVocalUrl={leadVocalUrl}
                finalRecordingUrl={finalRecordingUrl}
              />
            </div>
          )}

          {jsonData && activeTab === 'charts' && (
            <div className="p-6">
              <ReportCharts report={jsonData} />
            </div>
          )}

          {jsonData && activeTab === 'json' && (
            <pre className="text-sm text-white/90 bg-kod-purple-900/50 m-4 rounded-lg p-4 overflow-x-auto font-mono">
              {JSON.stringify(jsonData, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};
